import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { testimonials } from "@/mocks/home";

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.08 }
    );

    const elements = sectionRef.current?.querySelectorAll(".reveal");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-32 bg-background-50"
    >
      <div className="px-6 lg:px-12 xl:px-20">
        <div className="reveal text-center mb-16 md:mb-20">
          <span className="text-xs font-semibold tracking-[0.2em] text-primary-500 uppercase mb-4 block">
            Client Results
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 tracking-tight">
            {t("testimonials.title")}
          </h2>
          <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
            {t("testimonials.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="reveal bg-white border border-foreground-100/60 rounded-xl p-7 flex flex-col"
              style={{ transitionDelay: `${index * 80}ms` }}
            >
              <div className="flex items-center gap-0.5 mb-4">
                {Array.from({ length: testimonial.rating }).map((_, i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-amber-500 text-xs" />
                  </div>
                ))}
              </div>

              <blockquote className="text-sm text-foreground-600 leading-relaxed mb-6 flex-grow">
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>

              <div className="flex items-center gap-3 pt-5 border-t border-foreground-100/50">
                <div className="w-9 h-9 rounded-full bg-foreground-950 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white">
                    {testimonial.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900 leading-tight">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-foreground-400">
                    {testimonial.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}